import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useForm } from 'vee-validate'
import * as validation from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { loginWithCode } from '@/api/loginWithCode'
import { useAuth } from '@/stores/auth'
import { saveAuthToken } from '@/helpers/storage'
import { getPlayer } from '@/api/getPlayer'
import { toast } from '@/helpers/toast'
import { TYPE } from 'vue-toastification'
import { verifyCode } from '@/api/verifyCode'
import type { Player } from '@acepoker/contract'
import { useRoute, useRouter } from 'vue-router'
import { useFlags } from '@/composables/useFeatureFlags'
import { useGameState } from '@/stores/gameState'

// Constants
const RESEND_COUNTDOWN_VALUE = 60
const VERIFICATION_CODE_LENGTH = 4

export const useRedirectToLoginWithCode = () => {
  const flags = useFlags(['aip-436_onboarding_v.2'])
  const router = useRouter()
  const route = useRoute()
  const { player, status, token } = storeToRefs(useAuth())
  const { roomState } = storeToRefs(useGameState())
  const gameConfig = computed(() => roomState.value.gameConfig)

  const handleRedirect = () => {
    router.push('/login-compete?redirect=' + route.fullPath)
  }

  watch(
    () => [
      flags.isEnabled('aip-436_onboarding_v.2')?.value,
      status.value,
      gameConfig.value?.assessmentMode,
      roomState.value.handNumber
    ],
    async ([enabled, currentAuthStatus]) => {
      const gameRoomStateLoaded =
        typeof roomState.value.handNumber === 'number' && roomState.value.handNumber >= 0

      if (!token.value) {
        return handleRedirect()
      }

      if (!enabled || currentAuthStatus === 'loading' || currentAuthStatus === 'idle') {
        return
      }

      if (router.currentRoute.value.path.startsWith('/room/')) {
        if (!gameRoomStateLoaded) {
          return
        }
        if (gameConfig.value?.assessmentMode) {
          return
        }
      }

      if (!player.value?.email) {
        handleRedirect()
      }
    },
    { immediate: true }
  )
}

export const useLoginWithCode = (
  onSuccess: (player: Player) => void,
  onError?: (error: string) => void
) => {
  const { t } = useI18n()
  const auth = storeToRefs(useAuth())
  const isCodeSent = ref(false)
  const countdown = ref(RESEND_COUNTDOWN_VALUE)
  let timer: ReturnType<typeof setInterval> | null = null

  const formSchema = toTypedSchema(
    validation.object({
      email: validation.string().email(t('Errors.label.InvalidEmail')),
      code: validation
        .string()
        .length(VERIFICATION_CODE_LENGTH, t('Errors.label.InvalidCodeLength'))
    })
  )

  const { errors } = useForm({
    validationSchema: formSchema
  })

  const email = ref('')
  const verificationCode = ref<string[]>([])
  const pinError = ref('')

  const resetState = () => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
    countdown.value = RESEND_COUNTDOWN_VALUE
    verificationCode.value = []
    pinError.value = ''
  }

  const resetStateWithEmail = () => {
    resetState()
    email.value = ''
  }

  watch(isCodeSent, (newValue, oldValue) => {
    if (oldValue === true && newValue === false) {
      resetState() // Don't reset email when going back from code to email form
    }
  })

  const handleComplete = async (code: string[]) => {
    if (!email.value) {
      const errorMessage = t('Errors.label.EnterCode')
      pinError.value = errorMessage
      if (onError) onError(errorMessage)
      return
    }
    try {
      const { token } = await verifyCode(email.value, code.join(''))
      auth.token.value = token
      saveAuthToken(token)
      const player = await getPlayer()
      auth.player.value = player
      onSuccess(player)
      if (timer) {
        clearInterval(timer)
        timer = null
      }
      pinError.value = ''
    } catch (error: unknown) {
      let errorMessage = 'Unknown error occurred'

      if (error instanceof Error) {
        try {
          const errorData = JSON.parse(error.message)
          errorMessage = errorData.message
          pinError.value = errorMessage
        } catch {
          errorMessage = error.message
          toast({
            message: t('Errors.label.Oops'),
            details: errorMessage,
            icon: TYPE.ERROR
          })
        }
      }

      if (onError) onError(errorMessage)
    }
  }

  const onSendCode = async () => {
    if (!email.value) {
      return
    }
    try {
      await loginWithCode(email.value)
      isCodeSent.value = true
      countdown.value = RESEND_COUNTDOWN_VALUE
      startTimer()
      pinError.value = ''
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred'
      toast({
        message: t('Errors.label.Oops'),
        details: message,
        icon: TYPE.ERROR
      })
      if (onError) onError(message)
    }
  }

  const startTimer = () => {
    timer = setInterval(() => {
      if (countdown.value > 0) {
        countdown.value--
      } else {
        clearInterval(timer as ReturnType<typeof setInterval>)
        timer = null
      }
    }, 1000)
  }

  const close = () => {
    resetStateWithEmail()
    isCodeSent.value = false
  }

  onBeforeUnmount(() => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  })

  return {
    isCodeSent,
    countdown,
    email,
    verificationCode,
    pinError,
    errors,
    handleComplete,
    onSendCode,
    close
  }
}
